/**
 * Add Carries
 *
 * Records that one item carries part of the worth of an item it enables, with the
 * share as a percent (stored as a fraction). Writes one 'carries' edge through
 * the store's createEdge — the one app write path for relationship edges.
 * Before the save it says what is already carried of the target and refuses a
 * share that would take the total above 100%, in the words the table uses
 * (migration 999), so nothing known fails after the click (build contract §2.8).
 * Canon ref: 12-item-relationship-taxonomy-and-mutation-rules.md §5.11
 */

import { useState, useEffect } from 'react';
import { X, Weight, AlertCircle } from 'lucide-react';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { SearchableItemPicker } from '../SearchableItemPicker';
import {
  carriedShareOf,
  carriedSoFarSentence,
  formatCarriesShare,
  parseCarriesPercent,
} from '../../../lib/carriesRelationships';

/** Which end of the edge the item the dialog was opened from sits on. */
type CarriesMode = 'this_carries' | 'carried_by';

interface AddCarriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (edgeId: string) => void;
  /** The item the dialog was opened from. */
  anchorItemId: string;
}

const MODES: { value: CarriesMode; title: string; otherLabel: string; placeholder: string }[] = [
  {
    value: 'this_carries',
    title: 'This item carries part of another’s worth',
    otherLabel: 'The item it enables',
    placeholder: 'Search for the item this one makes possible…',
  },
  {
    value: 'carried_by',
    title: 'Another item carries part of this one’s worth',
    otherLabel: 'The item that carries it',
    placeholder: 'Search for the item that makes this one possible…',
  },
];

export function AddCarriesModal({ isOpen, onClose, onCreated, anchorItemId }: AddCarriesModalProps) {
  const createEdge = useItemRelationshipsStore((s) => s.createEdge);
  const edges = useItemRelationshipsStore((s) => s.edges);
  const fetchFromSupabase = useItemRelationshipsStore((s) => s.fetchFromSupabase);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const allItems = useItemsStore((s) => s.items);

  const [mode, setMode] = useState<CarriesMode>('this_carries');
  const [otherId, setOtherId] = useState('');
  const [percent, setPercent] = useState('');
  const [rationale, setRationale] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      void fetchItems();
      void fetchFromSupabase();
    }
  }, [isOpen, fetchItems, fetchFromSupabase]);

  useEffect(() => {
    if (!isOpen) return;
    setMode('this_carries');
    setOtherId('');
    setPercent('');
    setRationale('');
    setError(null);
  }, [isOpen, anchorItemId]);

  if (!isOpen) return null;

  const config = MODES.find((m) => m.value === mode) ?? MODES[0];
  const sourceItemId = mode === 'this_carries' ? anchorItemId : otherId;
  const targetItemId = mode === 'this_carries' ? otherId : anchorItemId;
  const titleOf = (id: string) => allItems.find((i) => i.id === id)?.title ?? 'that item';
  // What the target already has carried, read from the live edges before anything is typed.
  const soFar = otherId
    ? { alreadyCarried: carriedShareOf(targetItemId, edges), targetTitle: titleOf(targetItemId) }
    : undefined;
  const parsed = percent.trim() === '' ? null : parseCarriesPercent(percent, soFar);
  const magnitude = parsed && 'magnitude' in parsed ? parsed.magnitude : null;
  const existing = otherId
    ? edges.find(
        (e) =>
          e.type === 'carries' &&
          e.sourceItemId === sourceItemId &&
          e.targetItemId === targetItemId &&
          e.status !== 'dissolved' &&
          e.status !== 'dismissed',
      )
    : undefined;

  const handleCreate = async () => {
    setError(null);
    if (!otherId) {
      setError(`Choose ${config.otherLabel.toLowerCase()}.`);
      return;
    }
    const read = parseCarriesPercent(percent, soFar);
    if ('problem' in read) {
      setError(read.problem);
      return;
    }
    if (existing) {
      setError(
        `${mode === 'this_carries' ? 'This item' : `“${titleOf(otherId)}”`} already carries ${
          existing.magnitude != null ? formatCarriesShare(existing.magnitude) : 'a share'
        } of ${mode === 'this_carries' ? `“${titleOf(otherId)}”` : 'this item'}.`,
      );
      return;
    }

    setIsCreating(true);
    try {
      const edge = await createEdge({
        type: 'carries',
        directionality: 'directed',
        status: 'active',
        declaredMode: 'declared',
        sourceItemId,
        targetItemId,
        magnitude: read.magnitude,
        rationale: rationale.trim() || undefined,
      });
      if (edge) {
        onCreated?.(edge.id);
        onClose();
      } else {
        setError(
          (useItemRelationshipsStore.getState() as { syncError?: string | null }).syncError ??
            'The Carries relationship was not saved.',
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The Carries relationship was not saved.');
    } finally {
      setIsCreating(false);
    }
  };

  const canSubmit = !!otherId && magnitude != null && !existing && !isCreating;
  const share = magnitude != null ? formatCarriesShare(magnitude) : '…%';
  const reading = otherId
    ? mode === 'this_carries'
      ? `This item carries ${share} of the worth of “${titleOf(otherId)}”.`
      : `“${titleOf(otherId)}” carries ${share} of the worth of this item.`
    : null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <Weight className="h-6 w-6 text-[var(--text-ter)]" />
            <div>
              <h2 className="text-xl font-semibold text-[var(--text-pri)]">Add Carries</h2>
              <p className="text-sm text-[var(--text-ter)]">
                Part of the worth of an item it enables, and how much. It explains a rank; it does not say either item
                depends on the other, must be chosen, or comes first.
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-[var(--hover-tint)]" aria-label="Close">
            <X className="h-5 w-5 text-[var(--text-ter)]" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {error && (
            <div className="flex items-start gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)] text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            {MODES.map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => {
                  setMode(m.value);
                  setOtherId('');
                  setError(null);
                }}
                className={`border p-3 text-left text-sm font-medium text-[var(--text-pri)] transition-colors ${
                  mode === m.value ? 'border-[var(--slate)] bg-[var(--slate-dim)]' : 'border-[var(--rule)]'
                }`}
              >
                {m.title}
              </button>
            ))}
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">{config.otherLabel}</label>
            <SearchableItemPicker
              key={mode}
              value={otherId || undefined}
              onChange={(id) => {
                setOtherId(id ?? '');
                setError(null);
              }}
              excludeId={anchorItemId}
              label=""
              placeholder={config.placeholder}
              lifecycleStates={[]}
              allowEmpty={false}
            />
          </div>

          {soFar && <p className="text-sm text-[var(--text-sec)]">{carriedSoFarSentence(soFar)}</p>}

          <div>
            <label htmlFor="carries-share" className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">
              Share of its worth
            </label>
            <div className="flex items-center gap-2">
              <input
                id="carries-share"
                type="number"
                inputMode="decimal"
                min={0}
                max={100}
                step="any"
                value={percent}
                onChange={(e) => {
                  setPercent(e.target.value);
                  setError(null);
                }}
                placeholder="40"
                className="w-28 px-3 py-2 border border-[var(--rule)] text-sm"
              />
              <span className="text-sm text-[var(--text-sec)]">%</span>
            </div>
            {parsed && 'problem' in parsed && <p className="mt-1 text-xs text-[var(--coral)]">{parsed.problem}</p>}
          </div>

          {reading && <p className="text-sm text-[var(--text-pri)]">{reading}</p>}
          {existing && (
            <p className="text-sm text-[var(--mustard)]">
              Already recorded: {existing.magnitude != null ? formatCarriesShare(existing.magnitude) : 'a share'}.
            </p>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Why (optional)</label>
            <textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder="What about this item makes the other one worth having?"
              rows={2}
              className="w-full px-4 py-2 border border-[var(--rule)] resize-none text-sm"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-[var(--surface)]">
          <button type="button" onClick={onClose} className="px-4 py-2 text-[var(--text-sec)] font-medium hover:bg-[var(--hover-tint)]">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleCreate()}
            disabled={!canSubmit}
            className="px-4 py-2 text-white font-medium bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating ? 'Adding…' : 'Add Carries'}
          </button>
        </div>
      </div>
    </div>
  );
}
